import type { ChainId } from '@cryptopay/core';

export interface AdminApiOptions {
  readonly baseUrl: string;
  readonly apiKey: string;
  readonly fetcher?: typeof fetch;
}

export interface MerchantSummary {
  readonly merchantId: string;
  readonly name: string;
  readonly masterWallets: Partial<Record<ChainId, string>>;
  readonly webhookUrl: string | null;
  readonly allowedOrigins: readonly string[];
  readonly contactEmail: string | null;
  readonly status: string;
}

export interface AuditEvent {
  readonly eventId: string;
  readonly actorType: string;
  readonly actorId: string | null;
  readonly action: string;
  readonly targetType: string;
  readonly targetId: string | null;
  readonly payload: Record<string, unknown>;
  readonly createdAt: string;
}

export interface WebhookDelivery {
  readonly merchantId: string;
  readonly eventId: string;
  readonly status: number;
  readonly success: boolean;
  readonly attempts: number;
  readonly deliveredAt: string;
}

export interface WebhookDeadLetter {
  readonly merchantId: string;
  readonly eventId: string;
  readonly lastResult: { status: number; ok: boolean; body: string };
  readonly enqueuedAt: string;
}

export interface PendingChange {
  readonly changeId: string;
  readonly effectiveAt: string;
  readonly status: string;
  readonly proposedWallets: Partial<Record<ChainId, string>>;
}

export class AdminApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
  ) {
    super(message);
    this.name = 'AdminApiError';
  }
}

export class AdminApiClient {
  private readonly fetcher: typeof fetch;

  constructor(private readonly options: AdminApiOptions) {
    if (!options.baseUrl) throw new Error('AdminApiClient: baseUrl required');
    if (!options.apiKey) throw new Error('AdminApiClient: apiKey required');
    this.fetcher = options.fetcher ?? fetch.bind(globalThis);
  }

  async getMerchant(): Promise<MerchantSummary> {
    const { merchant } = await this.send<{ merchant: MerchantSummary }>(
      'GET',
      '/v1/merchants/me',
    );
    return merchant;
  }

  async listAuditEvents(): Promise<readonly AuditEvent[]> {
    const { events } = await this.send<{ events: AuditEvent[] }>(
      'GET',
      '/v1/admin/audit-events',
    );
    return events;
  }

  async listWebhooks(): Promise<{
    deliveries: WebhookDelivery[];
    deadLetters: WebhookDeadLetter[];
  }> {
    return this.send('GET', '/v1/admin/webhooks/recent');
  }

  async proposeMasterWallet(
    proposedWallets: Partial<Record<ChainId, string>>,
    proposedBy: string,
  ): Promise<{ change: PendingChange; confirmationToken: string }> {
    return this.send('POST', '/v1/admin/master-wallet/propose', {
      proposedWallets,
      proposedBy,
    });
  }

  async confirmMasterWallet(
    changeId: string,
    confirmationToken: string,
  ): Promise<{ merchantId: string; masterWallets: Partial<Record<ChainId, string>>; updatedAt: string }> {
    return this.send('POST', '/v1/admin/master-wallet/confirm', {
      changeId,
      confirmationToken,
    });
  }

  async cancelMasterWallet(changeId: string): Promise<{ change: { status: string } }> {
    return this.send('POST', '/v1/admin/master-wallet/cancel', { changeId });
  }

  private async send<T>(method: string, path: string, body?: unknown): Promise<T> {
    const url = `${this.options.baseUrl.replace(/\/$/, '')}${path}`;
    const headers: Record<string, string> = {
      'content-type': 'application/json',
      accept: 'application/json',
      'x-api-key': this.options.apiKey,
    };
    const init: RequestInit = { method, headers };
    if (body !== undefined) init.body = JSON.stringify(body);
    const res = await this.fetcher(url, init);
    const text = await res.text();
    let parsed: unknown;
    try {
      parsed = text.length > 0 ? JSON.parse(text) : null;
    } catch {
      throw new AdminApiError(
        `Invalid JSON from ${path}`,
        res.status,
        'INVALID_RESPONSE',
      );
    }
    if (!res.ok) {
      const error =
        isObject(parsed) && isObject(parsed.error) ? parsed.error : null;
      throw new AdminApiError(
        (error?.message as string) ?? `Request failed: ${res.status}`,
        res.status,
        (error?.code as string) ?? 'REQUEST_FAILED',
      );
    }
    if (isObject(parsed) && 'data' in parsed) {
      return parsed.data as T;
    }
    return parsed as T;
  }
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}
